import { connectToStores } from 'fluxible-addons-react';
import { RouteStore } from 'fluxible-router';
import Fuse from 'fuse.js';
import { each, filter } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { StickyContainer } from 'react-sticky';

import NavigationStore from '../../../stores/NavigationStore';
import QuickstartStore from '../../../stores/QuickstartStore';
import SearchField from '../../SearchField';
import Category from './components/Category';
import Sidebar from './components/Sidebar';
import LockCategories from './data/LockCategories';
import MgmtCategories from './data/MgmtCategories';
import styles from './styles.module.styl';

const FUSE_DEFAULTS = {
  threshold: 0.1,
  location: 0,
  maxPatternLength: 100,
  minMatchCharLength: 1,
  keys: ['title', 'name'],
};

class LibraryPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      libraries: props.librariesByCategory,
      noResults: false,
    };

    this.performSearch = this.performSearch.bind(this);
  }

  componentDidMount() {
    const { query } = this.props;

    if (query) {
      this.performSearch(query);
    }
  }

  performSearch(search) {
    const { librariesByCategory } = this.props;

    if (search.length === 0) {
      return this.setState({
        search,
        searchActive: false,
        noResults: false,
      });
    }

    let noResults = true;
    const libraries = {};
    each(librariesByCategory, (categorysLibraries, key) => {
      const results = new Fuse(categorysLibraries, FUSE_DEFAULTS).search(search);
      libraries[key] = results.map((result) => result.item);
      if (results.length) {
        noResults = false;
      }
    });

    return this.setState({
      search,
      searchActive: true,
      searchResults: libraries,
      noResults,
    });
  }

  render() {
    const { categories, librariesByCategory } = this.props;
    const { search, searchActive, searchResults, noResults } = this.state;

    const libraries = searchActive ? searchResults : librariesByCategory;

    return (
      <StickyContainer>
        <div className={`container ${styles.libraryPage}`}>
          <div className="row">
            <div className={`${styles.sidebarContainer} col-sm-3 hidden-xs`}>
              <Sidebar />
            </div>

            <div className={`${styles.libraryPage} col-sm-9 docs-content-container`}>
              <div className={styles.libraryHero}>
                <div className={styles.libraryHeroTitle}>
                  <h1>SDK Libraries</h1>
                  <p className={styles.hideOnMobile}>
                    Auth0 SDK libraries make it easy for developers to integrate and interact with Auth0. Explore any
                    library on GitHub, download a sample application, or use a quickstart for customized help.
                  </p>
                  <p className={styles.showOnMobile}>
                    Auth0 SDK libraries make it easy for developers to integrate and interact with Auth0.
                  </p>
                </div>
                <div className={styles.libraryHeroImage}>
                  <img src="/docs/img/library/header_img.svg" alt="library header" />
                </div>
              </div>

              <SearchField value={search} performSearch={this.performSearch} />

              <Category {...categories.spa} sdks={libraries.spa} />
              <Category {...categories.webapp} sdks={libraries.webapp} />
              <Category {...categories.backend} sdks={libraries.backend} />
              <Category {...categories.native} sdks={libraries.native} />
              <Category {...categories.mgmt} sdks={libraries.mgmt} />
              <Category {...categories.lock} sdks={libraries.lock} />

              {noResults && (
                <div className={styles.noResults}>
                  <h2>{`No libraries matches your search term "${search}".`}</h2>
                  <p>
                    There might be a spelling error in your search term, or we just don’t support that language yet.
                    Want to view a list of all libraries again? Clear your search term above.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </StickyContainer>
    );
  }
}

LibraryPage.propTypes = {
  query: PropTypes.string,
  categories: PropTypes.object.isRequired,
  librariesByCategory: PropTypes.object.isRequired,
};

export default connectToStores(LibraryPage, [RouteStore, NavigationStore, QuickstartStore], ({ getStore }) => {
  const { query: { term } = {} } = getStore(RouteStore).getCurrentRoute();
  const quickstarts = getStore(QuickstartStore).getQuickstarts();

  const categories = {
    lock: LockCategories,
    mgmt: MgmtCategories,
  };

  // hide beta quickstarts
  each(quickstarts, (category, name) => {
    categories[name] = {
      ...category,
      platforms: filter(category.platforms, (p) => p.beta !== true),
    };
  });

  const librariesByCategory = {};
  each(categories, (category, name) => {
    librariesByCategory[name] = filter(category.platforms, (p) => !!p.sdk).sort((a, b) =>
      a.title.localeCompare(b.title)
    );
  });

  return {
    query: term,
    categories,
    librariesByCategory,
  };
});
